import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import "@/features/vidyo-call/style/vidyo-call.css";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

/**
 * This hook manages the connection to the Vidyo room.
 * It is used in the CallPage component.
 *
 */
export function useJoinRoom() {
  const { key: roomKey } = useParams();
  const isVidyoInitStarted = useRef(false);
  const retryCount = useRef(0);
  const maxRetries = 3;

  const navigate = useNavigate();
  const vidyoSDK = useVidyoStore((store) => store.vidyoSDK);

  const pin = useVidyoStore((store) => store.pin);
  const displayName = useVidyoStore((store) => store.displayName);
  const setIsVidyoConnected = useVidyoStore(
    (store) => store.setIsVidyoConnected,
  );
  const setRoomKey = useVidyoStore((store) => store.setRoomKey);
  const roomKeyInStore = useVidyoStore((store) => store.roomKey);
  const shouldReturnToParentSession = useVidyoStore(
    (store) => store.breakoutSession?.shouldReturnToParentSession,
  );

  /**
   * Handles the failure of the connection to the Vidyo room.
   * It will retry the connection up to 3 times.
   */
  const handleFailure = useCallback(
    async (reason: string) => {
      console.error("[connectToRoomFailure] failure", reason);
      isVidyoInitStarted.current = false;
      if (retryCount.current < maxRetries) {
        const backoffTime = Math.pow(2, retryCount.current) * 1000; // Exponential backoff: 1s, 2s, 4s
        console.log(
          `[connectToRoomFailure] Retrying in ${backoffTime}ms (attempt ${retryCount.current + 1}/${maxRetries})`,
        );

        await new Promise((resolve) => setTimeout(resolve, backoffTime));
        retryCount.current++;

        // Retry connection
        await connectToRoom();
        return;
      }
      retryCount.current = 0; // Reset retry count for next time
      navigate(`/r/${roomKey}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, roomKey],
  );

  const handleDisconnected = useCallback(
    (reason: string) => {
      console.log("[handleDisconnected] disconnected", reason);

      switch (reason) {
        case "VIDYO_CONNECTORDISCONNECTREASON_Booted":
          navigate(`/r/${roomKey}/after-call?reason=removed`);
          break;
        case "VIDYO_CONNECTORDISCONNECTREASON_ConnectionLost":
        case "VIDYO_CONNECTORDISCONNECTREASON_ConnectionFailed":
          console.log("[handleDisconnected] connection lost");
          handleFailure(reason);
          break;
        default:
          // redirect to after-call page happens in the action-bar.tsx
          // navigate(`/r/${key}/after-call`);
          break;
      }
    },
    [navigate, roomKey, handleFailure],
  );

  const handleSuccess = useCallback(() => {
    setIsVidyoConnected(true);
    isVidyoInitStarted.current = false;
    retryCount.current = 0;
    console.log("[connectToRoomSuccess] connected");
  }, [setIsVidyoConnected]);

  /**
   * Connects to the Vidyo room as a guest
   */
  const connectToRoom = useCallback(async () => {
    if (!vidyoSDK || isVidyoInitStarted.current) {
      return;
    }

    if (roomKeyInStore !== roomKey) {
      console.log("currently-changing-room-cant-connect");
      return;
    }

    isVidyoInitStarted.current = true;

    try {
      const status = await vidyoSDK.GetState();

      console.log("[connectToRoom] status", status);

      if (status !== "VIDYO_CONNECTORSTATE_Ready") {
        console.log("[connectToRoom] waiting for 200ms");
        // wait here 200ms to ensure the sdk is disconnected
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      console.log("[connectToRoom] connecting...");

      await vidyoSDK.ConnectToRoomAsGuest({
        displayName: displayName,
        roomKey: roomKey ?? "",
        // roomPin: "failur",
        roomPin: pin ? pin.toString() : "",
        host: import.meta.env.VITE_VIDYO_PORTAL,
        onSuccess: handleSuccess,
        onDisconnected: handleDisconnected,
        onFailure: handleFailure,
      });
      console.log("[connectToRoom] connected");
    } catch (error) {
      console.error("[connectToRoom] Error connecting to room", error);
    }
  }, [
    vidyoSDK,
    roomKeyInStore,
    roomKey,
    displayName,
    pin,
    handleSuccess,
    handleDisconnected,
    handleFailure,
  ]);

  useEffect(() => {
    connectToRoom();
  }, [connectToRoom]);

  const changeRoom = useCallback(async () => {
    if (!vidyoSDK) {
      return;
    }

    console.log("[changeRoom] changing room");

    setIsVidyoConnected(false);
    await vidyoSDK.Disconnect();
    const status = await vidyoSDK.GetState();

    console.log("[changeRoom] call status", status);

    if (status !== "VIDYO_CONNECTORSTATE_Ready") {
      console.log("[changeRoom] waiting for 200ms");
      // wait here 200ms to ensure the sdk is disconnected
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    setRoomKey(roomKey);
    isVidyoInitStarted.current = false;

    if (shouldReturnToParentSession) {
      toast("Your mic is off after returning from the breakout room", {
        position: "bottom-left",
      });
    }
  }, [
    roomKey,
    setIsVidyoConnected,
    vidyoSDK,
    setRoomKey,
    shouldReturnToParentSession,
  ]);

  useEffect(() => {
    const roomKeyFromSlug = roomKey;

    if (roomKeyInStore !== roomKeyFromSlug) {
      changeRoom();
    }
  }, [roomKey, roomKeyInStore, changeRoom]);
}
